import { auth } from '../firebase';
import CryptoJS from 'crypto-js';

const ENCRYPTION_KEY = '26376a82-b60a-4fff-a514-78af7c0eef27';
const COOKIE_DOMAIN = '.usenode.com';

// Encrypt sensitive data before storing
const encryptData = (data) => {
  return CryptoJS.AES.encrypt(JSON.stringify(data), ENCRYPTION_KEY).toString();
};

// Decrypt data when retrieving
const decryptData = (encryptedData) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch (error) {
    console.error('Error decrypting data:', error);
    return null;
  }
};

// Set secure cookie that works across subdomains
const setSecureCookie = (name, value, hours = 24) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + hours * 60 * 60 * 1000);

  // For development on localhost
  const isLocalhost =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  let cookieString;
  if (isLocalhost) {
    // Development version - no secure flag, no domain restriction
    cookieString = `${name}=${encryptData(
      value
    )}; expires=${expires.toUTCString()}; path=/; samesite=lax`;
  } else {
    // Production version - changed samesite to lax for cross-subdomain navigation
    cookieString = `${name}=${encryptData(
      value
    )}; expires=${expires.toUTCString()}; domain=${COOKIE_DOMAIN}; path=/; secure; samesite=lax`;
  }

  document.cookie = cookieString;
};

// Get secure cookie
const getSecureCookie = (name) => {
  const nameEQ = name + '=';
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      const encryptedValue = cookie.substring(nameEQ.length, cookie.length);
      return decryptData(encryptedValue);
    }
  }
  return null;
};

// Clear secure cookie
const clearSecureCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${COOKIE_DOMAIN}; path=/;`;
};

// Generate version-specific subdomain URL
const getVersionSubdomain = (version) => {
  // Handle different version formats
  if (!version || version === 1) {
    return 'app.usenode.com'; // Default to app subdomain for v1 or no version
  }

  // For v2, v3, etc., use the version subdomain
  return `${version}.usenode.com`;
};

// Store authentication data in secure cookies
export const storeAuthDataInCookies = async (business, businessCode) => {
  try {
    const idToken = await auth.currentUser.getIdToken();

    const authData = {
      businessId: business.businessId,
      businessToken: business.token,
      firebaseToken: idToken,
      userId: auth.currentUser.uid,
      version: business.version || 'app',
      timestamp: Date.now(),
    };

    setSecureCookie('nodeAuthData', authData, 24);
    return true;
  } catch (error) {
    console.error('Error storing auth data in cookies:', error);
    return false;
  }
};

// Retrieve authentication data from secure cookies
export const getAuthDataFromCookies = () => {
  const authData = getSecureCookie('nodeAuthData');

  if (!authData) {
    return null;
  }

  // Check if token is expired (24 hours)
  const isExpired = Date.now() - authData.timestamp > 24 * 60 * 60 * 1000;
  if (isExpired) {
    clearSecureCookie('nodeAuthData');
    return null;
  }

  return authData;
};

// Clear authentication data from cookies
export const clearAuthDataFromCookies = () => {
  clearSecureCookie('nodeAuthData');
};

// Verify cookie was set and wait if needed
const verifyCookieSet = (cookieName, maxRetries = 5) => {
  return new Promise((resolve, reject) => {
    let retries = 0;

    const checkCookie = () => {
      const cookie = getSecureCookie(cookieName);
      if (cookie) {
        resolve(true);
      } else if (retries < maxRetries) {
        retries++;

        setTimeout(checkCookie, 50); // Check every 50ms
      } else {
        console.error('❌ Cookie verification failed after retries');
        reject(new Error('Cookie not set'));
      }
    };

    checkCookie();
  });
};

// Handle cross-domain redirect with cookies - now supports dynamic versioning
export const handleCrossDomainRedirect = async (business, businessCode) => {
  const success = await storeAuthDataInCookies(business, businessCode);
  console.log('success', success);
  if (success) {
    const version = business.version || 'app';
    const subdomain = getVersionSubdomain(version);
    const firebaseToken = await auth.currentUser.getIdToken();
    const params = generateAuthUrlParams(
      business,
      businessCode,
      firebaseToken,
      auth.currentUser.uid
    );

    try {
      await verifyCookieSet('nodeAuthData');
      window.location.href = `https://${subdomain}/auth-callback?${params}`;
    } catch (error) {
      console.error('Cookie verification failed, using fallback method');
      window.location.href = `https://${subdomain}/auth-callback?${params}`;
    }
  } else {
    throw new Error('Failed to store authentication data');
  }
};

// Generate URL parameters for fallback method
export const generateAuthUrlParams = (
  business,
  businessCode,
  firebaseToken,
  userId
) => {
  const authData = {
    businessId: business.businessId,
    businessToken: business.token,
    firebaseToken,
    userId,
    version: business.version || 'app',
  };

  const params = new URLSearchParams();
  Object.entries(authData).forEach(([key, value]) => {
    params.append(key, value);
  });

  return params.toString();
};

// Set up authentication on version-specific subdomain
export const setupAuthenticationFromCookies = () => {
  const authData = getAuthDataFromCookies();

  if (!authData) {
    return null;
  }

  const version = authData.version || 'app';
  const platformName = version === 'app' ? 'NODE' : `NODE_${version}`;

  // Set up localStorage and sessionStorage
  localStorage.setItem('businessId', authData.businessId);
  localStorage.setItem('userId', authData.userId);
  sessionStorage.setItem('businessToken', authData.businessToken);
  localStorage.setItem('platform', platformName);

  return authData;
};

// Utility function to check if current subdomain matches business version
export const isCorrectVersionSubdomain = (businessVersion) => {
  const currentHostname = window.location.hostname;
  const expectedSubdomain = getVersionSubdomain(businessVersion);

  return currentHostname === expectedSubdomain;
};

// Get redirect URL for a specific version
export const getVersionRedirectUrl = (version) => {
  const subdomain = getVersionSubdomain(version);
  return `https://${subdomain}/auth-callback`;
};

// DEBUG: Test function to check cookie setting
export const testCookieSet = () => {
  console.log('🧪 Testing cookie functionality...');

  const testData = {
    test: 'value',
    timestamp: Date.now(),
  };

  setSecureCookie('testCookie', testData, 1);

  // Try to read it back
  const retrieved = getSecureCookie('testCookie');
  console.log('📖 Retrieved cookie:', retrieved);

  return retrieved;
};

// Alternative: Use window.postMessage for cross-domain auth
export const handleCrossDomainRedirectWithPostMessage = async (
  business,
  businessCode
) => {
  const version = business.version || 'app';
  const subdomain = getVersionSubdomain(version);

  // Open target window
  const targetWindow = window.open(
    `https://${subdomain}/auth-callback`,
    '_self'
  );

  // Prepare auth data
  const idToken = await auth.currentUser.getIdToken();
  const authData = {
    businessId: business.businessId,
    businessToken: business.token,
    firebaseToken: idToken,
    userId: auth.currentUser.uid,
    version: business.version || 'app',
    timestamp: Date.now(),
  };

  // Send data via postMessage
  targetWindow.postMessage(
    {
      type: 'AUTH_DATA',
      data: authData,
    },
    `https://${subdomain}`
  );
};

// Listen for postMessage auth data (use on target subdomain)
export const setupPostMessageAuthListener = () => {
  window.addEventListener('message', (event) => {
    // Verify origin is from usenode.com
    if (!event.origin.includes('usenode.com')) {
      return;
    }

    if (event.data.type === 'AUTH_DATA') {
      const authData = event.data.data;
      console.log('📨 Received auth data via postMessage:', authData);

      // Set up authentication
      const version = authData.version || 'app';
      const platformName = version === 'app' ? 'NODE' : `NODE_${version}`;

      // Set up localStorage and sessionStorage
      localStorage.setItem('businessId', authData.businessId);
      localStorage.setItem('userId', authData.userId);
      sessionStorage.setItem('businessToken', authData.businessToken);
      localStorage.setItem('platform', platformName);

      // Also try to set cookie as backup
      setSecureCookie('nodeAuthData', authData, 24);

      console.log('✅ Authentication setup complete via postMessage');
    }
  });
};
